import React, { useRef } from 'react';
import * as Yup from 'yup';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import styled from 'styled-components';
import SmartLabel from 'components/v2/labels/SmartLabel';
import EditDialog from '../copy/dialogs/EditDialog';
import getErrorsLikeFormik from 'pages/admin/tools/edit/utils/getErrorsLikeFormik';
import TextareaWithMaxRows from './TextareaWithMaxRows';
import { COMMENT_MAX_LENGTH } from 'constants/constants';

export interface ConfirmWithCommentPayload {
  commentText: string;
}

interface TProps {
  isOpened: boolean;
  onClose(payload: ConfirmWithCommentPayload | null): void;

  header: string;
  help: React.ReactElement;
  inputLabelText: string;
  primaryButtonText: string;
  closable?: boolean;
  isCommentRequired?: boolean;
  maxLength?: number;
  validationSchema?: Yup.ObjectSchema<any>;
  commentMaxRows?: number;
}

const ConfirmWithCommentDialog = ({
  isOpened,
  onClose,
  header,
  help,
  inputLabelText,
  primaryButtonText,
  isCommentRequired,
  maxLength,
  closable,
  validationSchema,
  commentMaxRows,
}: TProps) => {
  const [commentText, setCommentText] = React.useState<string>('');
  const isDismissingRef = useRef<boolean>(false);
  const limit = maxLength ?? COMMENT_MAX_LENGTH;

  const isEmpty = !commentText?.trim();
  const schemaError =
    validationSchema && commentText ? getErrorsLikeFormik(validationSchema, { commentText }).commentText : undefined;
  const showRequiredIndicator = isCommentRequired && isEmpty;
  const shouldPaintRedBorder = !!schemaError;
  const inlineErrorText = schemaError;
  const disablePrimaryButton = (isCommentRequired && isEmpty) || !!schemaError;

  const handleDismiss = () => {
    isDismissingRef.current = true;
    onClose(null);
  };

  const handlePrimaryButtonClick = () => {
    isDismissingRef.current = false;
    onClose({ commentText });
  };

  return (
    <StyledEditDialog
      header={header}
      isOpened={isOpened}
      onHide={handleDismiss}
      transitionOptions={
        {
          onExited() {
            // Reset state only after closing animation has finished to avoid noticeable flickering
            if (!isDismissingRef.current) {
              setCommentText('');
            }
          },
        } as any
      }
      closable={closable}
    >
      {help}
      <StyledSmartLabel
        htmlFor="confirmDialogComment"
        label={inputLabelText}
        error={showRequiredIndicator ? 'Required' : undefined}
      />
      <CommentTextAreaWrapper>
        <TextareaWithMaxRows
          id="confirmDialogComment"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value.slice(0, limit))}
          style={{ width: '100%' }}
          rows={8}
          placeholder="Type here"
          maxLength={limit}
          maxRows={commentMaxRows ?? 8}
          hasError={shouldPaintRedBorder}
        />
        {inlineErrorText && <ErrorText>{inlineErrorText}</ErrorText>}
      </CommentTextAreaWrapper>
      <ButtonContainer>
        <SecondaryButton label="Cancel" onClick={handleDismiss} />
        <PrimaryButton label={primaryButtonText} onClick={handlePrimaryButtonClick} disabled={disablePrimaryButton} />
      </ButtonContainer>
    </StyledEditDialog>
  );
};

export const StyledEditDialog = styled(EditDialog)`
  max-width: 560px;
  & .p-dialog-header .p-dialog-title {
    font-family: Inter;
    font-size: 16px;
    color: ${(props) => props.theme.colors.primaryBlack};
  }
`;

export const StyledSmartLabel = styled(SmartLabel)`
  margin-top: 31px;
  margin-bottom: 4px;
`;

const CommentTextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ErrorText = styled.span`
  margin-top: 6px;
  color: ${(props) => props.theme.colors.systemStatusError};
  font-size: 13px;
  line-height: 18px;
  font-family: Inter, sans-serif;
`;

export const ButtonContainer = styled.div`
  margin-top: 30px;
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  gap: 16px;
`;

export default ConfirmWithCommentDialog;
